import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { CAT_MAP, formatDate } from "@/lib/categories";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { Seo, breadcrumbLd, SITE_URL, SITE_NAME } from "@/components/Seo";
import { ImageCredit } from "@/components/ImageCredit";
import { Compass, ListChecks, Wallet, AlertTriangle, BookMarked, ArrowRight } from "lucide-react";

const A = ({ to, children }) => (
  <Link to={to} className="font-semibold text-stone-900 underline decoration-gold decoration-2 underline-offset-2 hover:text-gold-soft transition-colors">
    {children}
  </Link>
);

const anchor = (i) => `sezione-${i + 1}`;

export default function StarterGuidePage() {
  const { category } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/starters/${category}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("missing"));
  }, [category]);

  const cat = CAT_MAP[category];

  if (state === "loading") {
    return <div className="max-w-3xl mx-auto px-4 py-20 animate-pulse space-y-4">
      <div className="h-10 bg-panel rounded-xl w-3/4" />
      <div className="h-4 bg-panel rounded w-full" />
      <div className="h-4 bg-panel rounded w-5/6" />
    </div>;
  }

  if (state === "missing") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="starter-missing">
        <Seo title="Guida non disponibile" path={`/come-iniziare/${category}`} noindex />
        <h1 className="font-editorial font-extrabold text-3xl text-stone-900">Guida non ancora disponibile</h1>
        <p className="mt-4 text-stone-600">
          Questa guida è in preparazione. Nel frattempo puoi leggere la{" "}
          <A to="/collezionismo">guida al collezionismo</A> o vedere{" "}
          <A to="/come-iniziare">tutte le guide per principianti</A>.
        </p>
      </div>
    );
  }

  const { guide, articles, value_cards: cards } = data;
  const faq = guide.faq || [];

  return (
    <article className="max-w-3xl mx-auto px-4 py-12 fade-up" data-testid="starter-guide-page">
      <Seo
        title={guide.title}
        description={guide.meta_description}
        path={`/come-iniziare/${category}`}
        type="article"
        image={guide.image_url}
        imageAlt={guide.title}
        published={guide.published_at}
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Come iniziare", path: "/come-iniziare" },
            { name: cat?.name || category, path: `/come-iniziare/${category}` },
          ]),
          {
            "@context": "https://schema.org", "@type": "Article",
            headline: guide.title?.slice(0, 110),
            description: guide.meta_description,
            inLanguage: "it-IT",
            datePublished: guide.published_at || guide.created_at,
            image: guide.image_url ? [guide.image_url.startsWith("/") ? `${SITE_URL}${guide.image_url}` : guide.image_url] : undefined,
            mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/come-iniziare/${category}` },
            author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
            articleSection: cat?.name || category,
          },
          ...(faq.length ? [{
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question", name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }] : []),
        ]}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Compass size={15} className="text-gold" />
        <span className="font-meta text-[10px] uppercase tracking-[0.24em] text-stone-500">Guida per principianti</span>
        {cat && (
          <Link to={`/categoria/${category}`} data-testid="starter-cat-badge"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cat.dot}`} />{cat.name}
          </Link>
        )}
      </div>

      <h1 className="mt-4 font-editorial font-extrabold text-4xl sm:text-5xl leading-[1.06] tracking-tight text-stone-900">
        {guide.title}
      </h1>
      {guide.published_at && (
        <p className="mt-3 font-meta text-[11px] uppercase tracking-wide text-stone-400">
          Aggiornata il {formatDate(guide.published_at)}
        </p>
      )}

      {guide.image_url && (
        <figure className="mt-6" data-testid="starter-cover">
          <div className="rounded-2xl overflow-hidden border-2 border-line bg-panel aspect-[16/9]">
            <img src={guide.image_url} alt={guide.title} loading="eager" decoding="async"
              className="w-full h-full object-cover" />
          </div>
          {guide.image_credit && (
            <figcaption className="mt-2">
              <ImageCredit credit={guide.image_credit} original={guide.image_ref} className="uppercase tracking-wide" testid="starter-image-credit" />
            </figcaption>
          )}
        </figure>
      )}

      <div className="mt-6 space-y-4 text-lg text-stone-600 leading-relaxed">
        {(guide.intro || "").split(/\n{2,}/).map((p, i) => <p key={i}>{p}</p>)}
      </div>

      {(guide.first_steps || []).length > 0 && (
        <div className="mt-8 rounded-2xl bg-panel border-2 border-line p-5" data-testid="starter-first-steps">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks size={14} className="text-gold" />
            <span className="font-meta text-[10px] uppercase tracking-[0.2em] text-stone-500">I primi cinque passi</span>
          </div>
          <ol className="space-y-2 list-decimal pl-5 text-stone-700 text-sm leading-relaxed">
            {guide.first_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}

      <nav className="mt-6 rounded-2xl bg-ink-deep border-2 border-line p-5" data-testid="starter-toc">
        <span className="font-meta text-[10px] uppercase tracking-[0.2em] text-stone-500">In questa guida</span>
        <ol className="mt-3 space-y-1.5">
          {(guide.sections || []).map((s, i) => (
            <li key={i}>
              <a href={`#${anchor(i)}`} className="text-sm text-stone-700 hover:text-gold transition-colors">
                {i + 1}. {s.heading}
              </a>
            </li>
          ))}
          {(guide.budget_tiers || []).length > 0 && <li><a href="#budget" className="text-sm text-stone-700 hover:text-gold transition-colors">Quanto budget serve</a></li>}
          {(guide.mistakes || []).length > 0 && <li><a href="#errori" className="text-sm text-stone-700 hover:text-gold transition-colors">Errori da evitare</a></li>}
          {faq.length > 0 && <li><a href="#faq" className="text-sm text-stone-700 hover:text-gold transition-colors">Domande frequenti</a></li>}
        </ol>
      </nav>

      <div className="mt-10 space-y-9">
        {(guide.sections || []).map((s, i) => (
          <section key={i} id={anchor(i)} data-testid={`starter-section-${i}`}>
            <h2 className="font-editorial font-bold text-lg md:text-lg text-stone-900">{i + 1}. {s.heading}</h2>
            <div className="mt-3 space-y-3 text-stone-600 leading-relaxed">
              {(s.content || "").split(/\n{2,}/).map((p, j) => <p key={j}>{p}</p>)}
            </div>
          </section>
        ))}

        {(guide.budget_tiers || []).length > 0 && (
          <section id="budget">
            <h2 className="font-editorial font-bold text-lg text-stone-900 flex items-center gap-2">
              <Wallet size={16} className="text-gold" /> Quanto budget serve davvero
            </h2>
            <div className="mt-4 space-y-3">
              {guide.budget_tiers.map((t, i) => (
                <div key={i} data-testid={`starter-budget-${i}`}
                  className={`rounded-2xl bg-panel border-2 border-line border-l-[6px] ${cat?.accent} p-4`}>
                  <span className="font-editorial font-bold text-stone-900">{t.label}</span>
                  <p className="text-sm text-stone-600 mt-1.5 leading-relaxed">{t.what}</p>
                </div>
              ))}
            </div>
            {cards?.length > 0 && (
              <p className="mt-4 text-stone-600 leading-relaxed">
                Per i prezzi dei singoli pezzi iconici vedi le <A to="/valori">Schede Valore</A>:{" "}
                {cards.slice(0, 3).map((c, i) => (
                  <span key={c.slug}>
                    {i > 0 && ", "}
                    <A to={`/valori/${c.slug}`}>{c.name}</A>
                  </span>
                ))}.
              </p>
            )}
          </section>
        )}

        {(guide.mistakes || []).length > 0 && (
          <section id="errori">
            <h2 className="font-editorial font-bold text-lg text-stone-900 flex items-center gap-2">
              <AlertTriangle size={16} className="text-gold" /> Gli errori da evitare
            </h2>
            <ol className="mt-3 space-y-2 text-stone-600 leading-relaxed list-decimal pl-5">
              {guide.mistakes.map((m, i) => <li key={i} data-testid={`starter-mistake-${i}`}>{m}</li>)}
            </ol>
          </section>
        )}

        {(guide.glossary || []).length > 0 && (
          <section id="glossario">
            <h2 className="font-editorial font-bold text-lg text-stone-900 flex items-center gap-2">
              <BookMarked size={16} className="text-gold" /> Il vocabolario minimo
            </h2>
            <dl className="mt-4 rounded-2xl bg-panel border-2 border-line divide-y divide-line">
              {guide.glossary.map((g, i) => (
                <div key={i} className="p-4" data-testid={`starter-glossary-${i}`}>
                  <dt className="font-semibold text-sm text-stone-900">{g.term}</dt>
                  <dd className="text-sm text-stone-600 mt-1 leading-relaxed">{g.definition}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {faq.length > 0 && (
          <section id="faq">
            <h2 className="font-editorial font-bold text-lg text-stone-900">Domande frequenti</h2>
            <div className="mt-4 space-y-3">
              {faq.map((f, i) => (
                <details key={i} className="rounded-2xl bg-panel border-2 border-line p-4" data-testid={`starter-faq-${i}`}>
                  <summary className="cursor-pointer font-semibold text-stone-900 text-sm marker:text-gold">{f.q}</summary>
                  <p className="mt-2.5 text-sm text-stone-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}
      </div>

      <section className="mt-12 rounded-2xl bg-ink-deep border-2 border-line p-5" data-testid="starter-next">
        <span className="font-meta text-[10px] uppercase tracking-[0.2em] text-stone-500">Continua da qui</span>
        <ul className="mt-3 space-y-2 text-sm text-stone-700">
          <li className="flex items-start gap-2"><ArrowRight size={14} className="text-gold mt-0.5 shrink-0" />
            Le regole valide per ogni ambito: <A to="/collezionismo">guida al collezionismo</A>
          </li>
          <li className="flex items-start gap-2"><ArrowRight size={14} className="text-gold mt-0.5 shrink-0" />
            Le altre categorie: <A to="/come-iniziare">tutte le guide per iniziare</A>
          </li>
          {cat && (
            <li className="flex items-start gap-2"><ArrowRight size={14} className="text-gold mt-0.5 shrink-0" />
              Le notizie aggiornate: <A to={`/categoria/${category}`}>{cat.name}</A>
            </li>
          )}
        </ul>
      </section>

      {articles?.length > 0 && (
        <section className="mt-8">
          <h2 className="font-editorial font-bold text-lg text-stone-900">Ultime notizie su {cat?.short?.toLowerCase() || "questo ambito"}</h2>
          <div className="mt-4 space-y-3">
            {articles.map((a) => (
              <Link key={a.slug} to={`/articolo/${a.slug}`} data-testid="starter-related-article"
                className="block rounded-2xl bg-panel border-2 border-line p-4 lift">
                <span className="font-semibold text-sm text-stone-900">{a.title}</span>
                <span className="block text-xs text-stone-500 mt-1 line-clamp-2">{a.summary}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <p className="mt-12 text-[11px] text-stone-400 leading-relaxed">
        Guida redazionale a scopo informativo, scritta con l'assistenza dell'AI e verificata dalla redazione.
        Le fasce di prezzo sono indicative e non costituiscono consulenza finanziaria né perizia. Dettagli nel{" "}
        <A to="/disclaimer">disclaimer</A>.
      </p>

      <div className="mt-10"><NewsletterSignup /></div>
    </article>
  );
}
